import Button from '@/components/UI/Button';
import InputField from '@/components/UI/InputField';
import { AuthContext } from '@/contexts/AuthContext';
import { AuthenticateControllerService, JWTToken, LoginVM } from '@/openapi';
import { useContext, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Login.module.css';
import { useRemotePost } from '@/hooks/useRemotePost';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '@/components/UI/Logo';
import Loader from '@/components/UI/Loader';
import SuccessToast from '@/components/UI/SuccessToast';
import FadeInSection from '@/components/UI/FadeInSection';
import { useViewportHeight } from '@/hooks/useViewportHeight';

const Login = () => {
	const { t } = useTranslation();
	useViewportHeight();
	const navigate = useNavigate();

	const authCtx = useContext(AuthContext);

	const [loginError, setLoginError] = useState(false);

	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');

	const userInputRef = useRef<HTMLInputElement>(null);
	const passwordInputRef = useRef<HTMLInputElement>(null);

	const auth = useRemotePost<JWTToken, { requestBody: LoginVM }>({
		remoteMethod: async (payload) => {
			return AuthenticateControllerService.authorize(payload);
		}
	});

	useEffect(() => {
		if (typeof auth.data !== 'undefined' && auth.data.id_token) {
			authCtx.login(auth.data.id_token);
		}
	}, [auth.data]);

	useEffect(() => {
		if (typeof auth.error !== 'undefined') {
			setLoginError(true);
		}
	}, [auth.error]);

	const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setLoginError(false);
		auth.resetError();

		if (!username.trim() || !password.trim()) {
			setLoginError(true);
			return;
		}

		const loginObject: LoginVM = { username, password };

		if (loginObject.username.trim() && loginObject.password.trim()) await auth.fetch({ requestBody: loginObject });
	};

	return (
		<div className={styles.main_container}>
			<form onSubmit={submitHandler} className={styles.form_container}>
				<Loader visible={auth.loading} />

				<FadeInSection className={styles.form_box}>
					<div className={styles.form_header}>
						<Logo className={styles.logo} onClick={() => navigate('/home')} />
					</div>

					<SuccessToast
						isVisible={loginError}
						message={t('login.wrongCredentials')}
						onClose={() => {
							setLoginError(false);
						}}
						type={'error'}
					/>

					<div className={styles.form_content}>
						<div className={styles.credentials_div}>
							<div className={styles.credential_couple}>
								<InputField
									type={'text'}
									title={t('login.usernameOrMail')}
									required
									label={t('login.username')}
									placeholder={t('login.usernameOrMail')}
									labelClassName={styles.inputLabel}
									ref={userInputRef}
									onChange={(e) => {
										setUsername(e.target.value);
									}}
									error={loginError}
								/>
							</div>
							<div className={styles.credential_couple}>
								<InputField
									type={'password'}
									title={t('login.password')}
									required
									label={t('login.password')}
									placeholder={t('login.password')}
									labelClassName={styles.inputLabel}
									ref={passwordInputRef}
									onChange={(e) => {
										setPassword(e.target.value);
									}}
									error={loginError}
								/>
							</div>
						</div>

						<Link to="/reset/password/init" className={styles.navLink}>
							<p className={styles.forgotPass_label}>{t('login.forgotPassword')}</p>
						</Link>

						<div className={styles.box_btn}>
							<Button
								className={styles.btn_login}
								typeSubmit={'submit'}
								onClick={() => {}}
								name={t('login.signIn')}
								color="black"
								background="white"
							/>
						</div>

						<Link to="/signup" className={styles.navLink}>
							<p className={styles.forgotPass_label}>{t('login.signUpLink')}</p>
						</Link>
					</div>
				</FadeInSection>
			</form>
		</div>
	);
};

export default Login;
